// Canonical modality + position-role + verified-pairing map for Kula Create.
//
// Source: Decision History §5.3 / KULA-CREATE-ARCHITECTURE.md "Position rules"
// + IMPLEMENTATION-PLAN.md "5-modality cluster map". A 5th modality (Apply)
// was added on top of Dylan's original 4 to distinguish practice (Challenge)
// from transfer to a real-world context (Apply). See PRESERVATION-AUDIT.md
// for the load-bearing reasoning.
//
// One source of truth — the Analyst prompt, Designer-spec prompt, conversational
// Designer prompt, and Workbench UI all read from here. Do not duplicate.

export type ModalityKey = 'scenario' | 'challenge' | 'apply' | 'reflection' | 'explore';

export const MODALITY_KEYS: readonly ModalityKey[] = [
  'scenario',
  'challenge',
  'apply',
  'reflection',
  'explore',
] as const;

export type InteractionTypeId =
  | 'reveal-predict'
  | 'scenario-simulation'
  | 'spot-the-error'
  | 'drag-sort'
  | 'gallery-walk'
  | 'timeline-navigator'
  | 'hotspot-explorer'
  | 'confidence-meter'
  | 'progressive-builder'
  | 'guided-sandbox'
  | 'scaffolded-investigation'
  | 'perspective-lenses'
  | 'tension-mapper'
  | 'before-after-bridge'
  | 'claim-evidence'
  | 'layered-reveal'
  | 'reflect-act'
  | 'teach-back';

export type PositionRole = 'opener' | 'core' | 'closer';

export interface ModalityDefinition {
  key: ModalityKey;
  label: string;
  tradition: string;
  /** Plain-language hint the conversational Designer can read aloud. Third
   *  person — it describes the learner's experience to the instructor/planner. */
  blurb: string;
  /** Second-person version shown to the LEARNER (e.g. the Attune "how do you
   *  want to learn this?" cards), where "you'll…" reads right and "the learner"
   *  does not. Keep in sync with `blurb`. */
  learnerBlurb: string;
  /** Signals in the content / brief that point to this modality. */
  contentSignals: string[];
  /** Interaction types in this modality's cluster, ranked best-fit first. */
  cluster: InteractionTypeId[];
}

export const MODALITIES: Record<ModalityKey, ModalityDefinition> = {
  scenario: {
    key: 'scenario',
    label: 'Scenario',
    tradition: 'Experiential learning (Kolb), situated cognition, case-based reasoning',
    blurb: 'Decisions under stakes — the learner walks through "what would I do?"',
    learnerBlurb: 'Decisions under stakes — you walk through "what would I do?"',
    contentSignals: [
      'A realistic situation with a role and stakes',
      '"What would you do" framing',
      'Branching decisions with consequences',
    ],
    cluster: ['scenario-simulation', 'tension-mapper', 'perspective-lenses'],
  },
  challenge: {
    key: 'challenge',
    label: 'Challenge',
    tradition: 'Problem-based learning, deliberate practice (Ericsson), constructionism',
    blurb: 'A concrete task to perform — the learner does, then reads.',
    learnerBlurb: 'A concrete task — you try it first, then read.',
    contentSignals: [
      'A deliverable or skill to produce',
      '"Build / write / create / practise" verbs',
      'Frameworks the learner applies to a task',
    ],
    cluster: ['guided-sandbox', 'progressive-builder', 'spot-the-error', 'teach-back'],
  },
  apply: {
    key: 'apply',
    label: 'Apply',
    tradition: 'Transfer of learning (Perkins & Salomon), far-transfer, authentic application',
    blurb: 'Take it to the real world — the learner uses this in their actual role next week.',
    learnerBlurb: 'Take it to the real world — you use this in your actual role next week.',
    contentSignals: [
      'Concrete examples from the learner\'s own context',
      '"In your role / on your team / for your project" framing',
      'Evidence-building or planning toward a real upcoming action',
    ],
    cluster: ['scenario-simulation', 'claim-evidence', 'scaffolded-investigation'],
  },
  reflection: {
    key: 'reflection',
    label: 'Reflection',
    tradition: 'Reflective practice (Schön), Kolb, Liberating Structures W³, Dewey',
    blurb: 'Process something already lived — the learner makes meaning of an experience.',
    learnerBlurb: "Process something you've lived — you make meaning of an experience.",
    contentSignals: [
      'Case studies, incidents, lessons learned, debriefs',
      'Material the learner has already experienced',
      'After-action / retrospective framing',
    ],
    cluster: ['reflect-act', 'before-after-bridge', 'confidence-meter'],
  },
  explore: {
    key: 'explore',
    label: 'Explore',
    tradition: 'Inquiry-based (Dewey), self-directed (Knowles), constructivism (Piaget)',
    blurb: 'A topic to survey before drilling in — the learner sets their own path.',
    learnerBlurb: 'A topic to survey before drilling in — you set your own path.',
    contentSignals: [
      'A broad topic with several parallel facets or cases',
      'No strict order — learner chooses the route',
      'Diagrams, maps, multiple perspectives to compare',
    ],
    // Hotspot Explorer restored as the first-choice spatial mechanism per
    // Dylan's Decision History §5.3. Cluster now matches the original spec.
    cluster: ['hotspot-explorer', 'gallery-walk', 'scaffolded-investigation'],
  },
};

// ---- Position roles ----------------------------------------------------------
// Modality-independent — these constrain where in a multi-slot lesson a given
// interaction type can sit. Lifted from KULA-CREATE-ARCHITECTURE.md §"Position
// rules".

export const POSITION_RULES: Record<PositionRole, InteractionTypeId[]> = {
  opener: ['reveal-predict', 'confidence-meter', 'before-after-bridge'],
  core: [
    'scenario-simulation',
    'spot-the-error',
    'drag-sort',
    'gallery-walk',
    'timeline-navigator',
    'hotspot-explorer',
    'progressive-builder',
    'scaffolded-investigation',
    'perspective-lenses',
    'tension-mapper',
    'claim-evidence',
    'layered-reveal',
    'teach-back',
  ],
  closer: ['guided-sandbox', 'reflect-act'],
};

/** Returns the valid roles for an interaction type. Most belong to exactly one role. */
export function rolesFor(type: InteractionTypeId): PositionRole[] {
  return (['opener', 'core', 'closer'] as PositionRole[]).filter((r) =>
    POSITION_RULES[r].includes(type),
  );
}

// ---- Verified pairings -------------------------------------------------------
// Cross-slot pairings Dylan validated. Designer should prefer these when the
// outcome fits. (Lifted from KULA-CREATE-ARCHITECTURE.md §"Verified pairings".)

export interface VerifiedPairing {
  first: InteractionTypeId;
  second: InteractionTypeId;
  arc: string;
}

export const VERIFIED_PAIRINGS: VerifiedPairing[] = [
  { first: 'scenario-simulation', second: 'reflect-act', arc: 'decision-making' },
  { first: 'reveal-predict', second: 'perspective-lenses', arc: 'misconceptions then depth' },
  { first: 'layered-reveal', second: 'claim-evidence', arc: 'uncover then argue' },
  { first: 'confidence-meter', second: 'before-after-bridge', arc: 'metacognitive' },
  { first: 'tension-mapper', second: 'reflect-act', arc: 'dilemma then values' },
];

// ---- Soft enforcement helpers (warn-only — see IMPLEMENTATION-PLAN §scope) ---

export interface SequenceWarning {
  code: 'reflect-act-not-first' | 'opener-out-of-position' | 'closer-out-of-position' | 'off-cluster';
  slotIndex: number;
  message: string;
}

/**
 * Soft-validate a sequence of interaction types against position rules and an
 * optional lesson modality. Returns warnings — never throws. Used to render
 * advisory chips in the UI and to log to the Designer's reasoning.
 */
export function warnSequence(
  sequence: InteractionTypeId[],
  modality?: ModalityKey,
): SequenceWarning[] {
  const warnings: SequenceWarning[] = [];
  sequence.forEach((type, i) => {
    const roles = rolesFor(type);
    if (type === 'reflect-act' && i === 0) {
      warnings.push({
        code: 'reflect-act-not-first',
        slotIndex: i,
        message:
          'reflect-act is a debrief — it needs something to debrief. Move it later, or pick a different opener.',
      });
    }
    if (roles.length === 1 && roles[0] === 'opener' && i !== 0) {
      warnings.push({
        code: 'opener-out-of-position',
        slotIndex: i,
        message: `${type} works best as the first interactive slot.`,
      });
    }
    if (roles.length === 1 && roles[0] === 'closer' && i !== sequence.length - 1) {
      warnings.push({
        code: 'closer-out-of-position',
        slotIndex: i,
        message: `${type} works best as the final interactive slot.`,
      });
    }
    if (modality && !MODALITIES[modality].cluster.includes(type)) {
      warnings.push({
        code: 'off-cluster',
        slotIndex: i,
        message: `${type} is outside the ${MODALITIES[modality].label} cluster. Confirm the modality choice or the type — they may not be telling the same story.`,
      });
    }
  });
  return warnings;
}
